import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, DollarSign, TrendingUp, Building2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { ROUTE_PATHS } from '@/lib/index';
import { taxDeductions } from '@/lib/dashboard-data';
import { springPresets } from '@/lib/motion';

export default function TaxStrategy() {
  const [claimedDeductions, setClaimedDeductions] = useState<string[]>([]);

  const totalFound = taxDeductions.reduce((sum, d) => sum + d.amount, 0);
  const totalClaimed = taxDeductions
    .filter((d) => claimedDeductions.includes(d.id))
    .reduce((sum, d) => sum + d.amount, 0);
  const totalUnclaimed = totalFound - totalClaimed;

  const toggleClaimed = (id: string) => {
    setClaimedDeductions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const advancedStrategies = [
    {
      id: '1',
      title: 'Augusta Rule (Section 280A)',
      description: 'Rent your home to your business for up to 14 days per year tax-free. Perfect for board meetings, client events, or strategic planning sessions.',
      savings: '$6,000',
      requirements: ['Own your home', 'Legitimate business purpose', 'Fair market rental rate', 'Proper documentation'],
      difficulty: 'Medium',
    },
    {
      id: '2',
      title: 'S-Corp Election',
      description: 'Convert your LLC to S-Corp status to save on self-employment taxes. Pay yourself a reasonable salary and take the rest as distributions.',
      savings: '$5,200',
      requirements: ['$60k+ net income', 'Payroll setup', 'Reasonable salary determination', 'Quarterly payroll taxes'],
      difficulty: 'Hard',
    },
    {
      id: '3',
      title: 'Hire Your Children',
      description: 'Employ your kids (under 18) in your business. Their wages are deductible to you and tax-free to them (up to standard deduction).',
      savings: '$3,800',
      requirements: ['Legitimate work', 'Reasonable pay', 'Proper documentation', 'W-2 or 1099 filing'],
      difficulty: 'Easy',
    },
    {
      id: '4',
      title: 'Qualified Business Income (QBI) Deduction',
      description: 'Deduct up to 20% of your qualified business income if you\'re a pass-through entity (LLC, S-Corp, sole prop).',
      savings: '$12,000',
      requirements: ['Pass-through entity', 'Taxable income under $383k (married)', 'Qualified trade or business', 'W-2 wages or property basis'],
      difficulty: 'Medium',
    },
  ];

  const entityComparison = [
    { entity: 'Sole Proprietor', income: 100000, selfEmploymentTax: 15300, incomeTax: 18289, totalTax: 33589 },
    { entity: 'LLC (Single Member)', income: 100000, selfEmploymentTax: 15300, incomeTax: 18289, totalTax: 33589 },
    { entity: 'S-Corp', income: 100000, selfEmploymentTax: 4590, incomeTax: 18289, totalTax: 22879 },
    { entity: 'C-Corp', income: 100000, selfEmploymentTax: 0, incomeTax: 21000, totalTax: 21000 },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to={ROUTE_PATHS.DASHBOARD}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Tax Strategy Module</h1>
              <p className="text-muted-foreground mt-1">Find deductions, optimize entity structure, save thousands</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* KPI Cards */}
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={springPresets.gentle}>
            <Card className="p-6 bg-accent/10 border-accent/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Found</p>
                  <p className="text-2xl font-bold font-mono text-accent">${(totalFound / 1000).toFixed(1)}k</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
          >
            <Card className="p-6 bg-primary/10 border-primary/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Claimed</p>
                  <p className="text-2xl font-bold font-mono text-primary">${(totalClaimed / 1000).toFixed(1)}k</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Still Unclaimed</p>
                  <p className="text-2xl font-bold font-mono">${(totalUnclaimed / 1000).toFixed(1)}k</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="scanner" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="scanner">Deduction Scanner</TabsTrigger>
            <TabsTrigger value="strategies">Advanced Strategies</TabsTrigger>
            <TabsTrigger value="optimizer">Entity Optimizer</TabsTrigger>
          </TabsList>

          {/* Deduction Scanner Tab */}
          <TabsContent value="scanner" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-6">AI-Detected Deductions</h3>
              <div className="space-y-4">
                {taxDeductions.map((deduction, index) => (
                  <motion.div
                    key={deduction.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ ...springPresets.gentle, delay: index * 0.05 }}
                    className="p-4 rounded-lg border border-border hover:border-accent/50 transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold">{deduction.category}</h4>
                          <Badge variant="outline" className="border-accent text-accent">
                            {deduction.confidence}% confidence
                          </Badge>
                          <Badge variant="outline" className="border-primary text-primary font-mono">
                            ${deduction.amount.toLocaleString()}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{deduction.strategy}</p>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={claimedDeductions.includes(deduction.id)}
                            onCheckedChange={() => toggleClaimed(deduction.id)}
                          />
                          <span className="text-sm text-muted-foreground">
                            {claimedDeductions.includes(deduction.id) ? 'Claimed' : 'Mark as claimed'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Advanced Strategies Tab */}
          <TabsContent value="strategies" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              {advancedStrategies.map((strategy, index) => (
                <motion.div
                  key={strategy.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full">
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="text-lg font-semibold">{strategy.title}</h4>
                      <Badge
                        className={
                          strategy.difficulty === 'Easy'
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : strategy.difficulty === 'Medium'
                            ? 'bg-accent/10 text-accent border-accent/20'
                            : 'bg-destructive/10 text-destructive border-destructive/20'
                        }
                      >
                        {strategy.difficulty}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{strategy.description}</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg">
                        <span className="text-sm font-medium">Potential Savings</span>
                        <span className="text-lg font-bold text-accent font-mono">{strategy.savings}</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-2">Requirements:</p>
                        <ul className="space-y-1">
                          {strategy.requirements.map((req, i) => (
                            <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Entity Optimizer Tab */}
          <TabsContent value="optimizer" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-2">Entity Structure Comparison</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Based on $100,000 net business income • Married filing jointly
              </p>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-semibold">Entity Type</th>
                      <th className="text-right py-3 px-4 font-semibold">Self-Employment Tax</th>
                      <th className="text-right py-3 px-4 font-semibold">Income Tax</th>
                      <th className="text-right py-3 px-4 font-semibold">Total Tax</th>
                      <th className="text-right py-3 px-4 font-semibold">Savings vs Sole Prop</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entityComparison.map((entity, index) => {
                      const savings = entityComparison[0].totalTax - entity.totalTax;
                      const isRecommended = entity.entity === 'S-Corp';
                      return (
                        <motion.tr
                          key={entity.entity}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                          className={`border-b border-border ${isRecommended ? 'bg-primary/5' : ''}`}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{entity.entity}</span>
                              {isRecommended && (
                                <Badge variant="outline" className="border-primary text-primary">
                                  Recommended
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-right py-4 px-4 font-mono">${entity.selfEmploymentTax.toLocaleString()}</td>
                          <td className="text-right py-4 px-4 font-mono">${entity.incomeTax.toLocaleString()}</td>
                          <td className="text-right py-4 px-4 font-mono font-semibold">${entity.totalTax.toLocaleString()}</td>
                          <td className="text-right py-4 px-4">
                            {savings > 0 ? (
                              <span className="text-primary font-mono font-semibold">+${savings.toLocaleString()}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-primary mb-1">S-Corp Recommendation</h4>
                    <p className="text-sm text-muted-foreground">
                      Based on your $100k income, electing S-Corp status could save you <span className="font-bold text-primary">$10,710/year</span> in self-employment taxes. You'd pay yourself a reasonable salary of $60k and take $40k as distributions (not subject to SE tax).
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
